import { EntityType } from '../models/InvestmentEntity';

export interface FieldDefinition {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'textarea' | 'select';
  required?: boolean;
  placeholder?: string;
  helperText?: string;
  options?: { value: string; label: string }[];
  validation?: {
    pattern?: RegExp;
    message?: string;
  };
}

const TRUSTEE_TYPE_OPTIONS = [
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'CORPORATE', label: 'Corporate' },
];

const ABN_PLACEHOLDER = 'e.g., 12 345 678 901';
const ABN_HELPER_TEXT = 'Australian Business Number';

function createAbnField(): FieldDefinition {
  return {
    key: 'abn',
    label: 'ABN',
    type: 'text',
    placeholder: ABN_PLACEHOLDER,
    helperText: ABN_HELPER_TEXT,
  };
}

function createTrustLikeEntityFields(options: {
  trusteeNameHelper: string;
  trustNameLabel: string;
  trustNamePlaceholder: string;
  trustNameHelper: string;
}): FieldDefinition[] {
  return [
    {
      key: 'trusteeName',
      label: 'Trustee Name',
      type: 'text',
      placeholder: 'e.g., John Smith',
      helperText: options.trusteeNameHelper,
    },
    {
      key: 'trustName',
      label: options.trustNameLabel,
      type: 'text',
      required: true,
      placeholder: options.trustNamePlaceholder,
      helperText: options.trustNameHelper,
    },
    createAbnField(),
    {
      key: 'trusteeType',
      label: 'Trustee Type',
      type: 'select',
      options: TRUSTEE_TYPE_OPTIONS,
      helperText: 'Type of trustee structure',
    },
  ];
}

// Simplified field definitions matching exact backend model requirements
export const ENTITY_FIELD_DEFINITIONS: Record<EntityType, FieldDefinition[]> = {
  INDIVIDUAL: [
    {
      key: 'firstName',
      label: 'First Name',
      type: 'text',
      required: true,
      placeholder: 'e.g., John',
      helperText: "Individual's first name",
    },
    {
      key: 'lastName',
      label: 'Last Name',
      type: 'text',
      required: true,
      placeholder: 'e.g., Smith',
      helperText: "Individual's last name",
    },
  ],
  COMPANY: [
    {
      key: 'companyName',
      label: 'Company Name',
      type: 'text',
      required: true,
      placeholder: 'e.g., ABC Pty Ltd',
      helperText: 'Full legal company name',
    },
    {
      key: 'acn',
      label: 'ACN',
      type: 'text',
      placeholder: 'e.g., 123 456 789',
      helperText: 'Australian Company Number',
    },
    createAbnField(),
  ],
  PARTNERSHIP: [
    {
      key: 'companyName',
      label: 'Partnership Name',
      type: 'text',
      required: true,
      placeholder: 'e.g., ABC Partnership',
      helperText: 'Full legal partnership name',
    },
    createAbnField(),
  ],
  TRUST: createTrustLikeEntityFields({
    trusteeNameHelper: 'Name of the trustee',
    trustNameLabel: 'Trust Name',
    trustNamePlaceholder: 'e.g., Smith Family Trust',
    trustNameHelper: 'Full legal trust name',
  }),
  SMSF: createTrustLikeEntityFields({
    trusteeNameHelper: 'Name of the SMSF trustee',
    trustNameLabel: 'SMSF Name',
    trustNamePlaceholder: 'e.g., Smith Family Super Fund',
    trustNameHelper: 'Full legal SMSF name',
  }),
};

export const getFieldDefinitionsForEntityType = (
  entityType?: EntityType | null,
): FieldDefinition[] => {
  if (!entityType) {
    return [];
  }

  return ENTITY_FIELD_DEFINITIONS[entityType] || [];
};

function validateRequiredField(
  def: FieldDefinition,
  value: unknown,
  errors: Record<string, string>,
): boolean {
  if (def.required && (!value || value.toString().trim() === '')) {
    errors[def.key] = `${def.label} is required`;
    return false;
  }
  return !(!value || value.toString().trim() === '');
}

function validateFieldDefinition(
  def: FieldDefinition,
  value: unknown,
  errors: Record<string, string>,
): void {
  if (!validateRequiredField(def, value, errors)) {
    return;
  }

  const textValue = String(value);

  if (def.type === 'number' && isNaN(Number(value))) {
    errors[def.key] = `${def.label} must be a valid number`;
    return;
  }

  if (def.type === 'date') {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(textValue) || isNaN(Date.parse(textValue))) {
      errors[def.key] = `${def.label} must be a valid date (YYYY-MM-DD)`;
    }
    return;
  }

  if (def.validation?.pattern && !def.validation.pattern.test(textValue)) {
    errors[def.key] = def.validation.message || `${def.label} format is invalid`;
  }
}

function validateTrusteeFields(fields: Record<string, any>, errors: Record<string, string>): void {
  if (fields.trusteeType === 'INDIVIDUAL') {
    if (
      !fields.trusteeIndividuals ||
      !Array.isArray(fields.trusteeIndividuals) ||
      fields.trusteeIndividuals.length === 0
    ) {
      errors.trusteeIndividuals =
        'At least one trustee individual is required when trustee type is Individual';
      return;
    }
    if (fields.trusteeIndividuals.length > 4) {
      errors.trusteeIndividuals = 'Maximum of 4 trustee individuals allowed';
      return;
    }
    fields.trusteeIndividuals.forEach((individual: any, index: number) => {
      if (!individual?.firstName) {
        errors[`trusteeIndividual${index}FirstName`] =
          `First name is required for trustee individual ${index + 1}`;
      }
      if (!individual?.lastName) {
        errors[`trusteeIndividual${index}LastName`] =
          `Last name is required for trustee individual ${index + 1}`;
      }
    });
    return;
  }

  if (fields.trusteeType !== 'CORPORATE') {
    return;
  }

  if (!fields.trusteeCorporate) {
    errors.trusteeCorporate =
      'Corporate trustee details are required when trustee type is Corporate';
    return;
  }

  if (!fields.trusteeCorporate.companyName) {
    errors.trusteeCorporateCompanyName = 'Company name is required for corporate trustee';
  }
  if (!fields.trusteeCorporate.acn) {
    errors.trusteeCorporateAcn = 'ACN is required for corporate trustee';
  }
}

export const validateEntitySpecificFields = (
  entityType: EntityType,
  fields: Record<string, any>,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const definitions = getFieldDefinitionsForEntityType(entityType);

  definitions.forEach((def) => validateFieldDefinition(def, fields[def.key], errors));

  if ((entityType === 'TRUST' || entityType === 'SMSF') && fields.trusteeType) {
    validateTrusteeFields(fields, errors);
  }

  return errors;
};
